import { PortExtended } from '@ais-ui/api-spec/portserver';
import { usePortAdminGet } from '@ais-ui/api';
import {
  shapeToVectorLayer,
  useLayers,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiEye, PiEyeClosed } from 'react-icons/pi';

interface ShowHidePortButtonProps {
  portId: number;
  portName: string;
  locode?: string;
}

export const PORT_ADMIN_LAYER_PREFIX = 'port-admin-';
export const PORT_ADMIN_INNER_LAYER_PREFIX = 'port-admin-inner-';

/**
 * Creates a GeoJSON shape for the outer port boundary.
 */
const portOuterToGeoJson = (port: PortExtended): GeoJson | null => {
  if (!port.outer) return null;

  return {
    type: 'Feature',
    properties: {
      id: `port-outer-${port.id}`,
      name: port.name || 'Port',
      color: 'blue',
      transparency: 0.3,
      type: 'port-outer',
      portId: port.id,
      locode: port.locode,
      country: port.country,
    },
    geometry: port.outer as GeoJson['geometry'],
  };
};

/**
 * Creates a GeoJSON FeatureCollection for the inner areas of a port.
 * These are rendered on a separate layer on top of the outer boundary with a distinct color.
 */
const portInnerToGeoJson = (port: PortExtended): GeoJson | null => {
  if (!port.inner || port.inner.length === 0) return null;

  const features: GeoJson[] = [];

  port.inner.forEach((innerArea) => {
    if (innerArea.polygon) {
      features.push({
        type: 'Feature',
        properties: {
          id: `port-inner-${innerArea.id}`,
          name: innerArea.name || 'Inner Area',
          color: 'orange',
          transparency: 0.3,
          type: 'port-inner',
          terminalType: innerArea.terminalType,
          portId: port.id,
          portName: port.name,
          locode: port.locode,
          country: port.country,
        },
        geometry: innerArea.polygon as GeoJson['geometry'],
      });
    }
  });

  if (features.length === 0) return null;
  if (features.length === 1) return features[0];

  return {
    type: 'FeatureCollection',
    properties: {
      id: `port-admin-inner-${port.id}`,
      name: `${port.name || 'Port'} - Inner Areas`,
      color: 'orange',
      transparency: 0.3,
      type: 'port-inner',
    },
    features,
  };
};

export const ShowHidePortButton = ({
  portId,
  portName,
  locode,
}: ShowHidePortButtonProps) => {
  const { addLayer, zoomToLayer, doesLayerExist, removeLayerById } =
    useLayers();
  const { displayProjection, dataProjection } = useOpenLayersContext();
  const [isLoading, setIsLoading] = useState(false);

  // We only fetch when needed (not enabled by default)
  const { refetch } = usePortAdminGet(portId);

  const layerId = `${PORT_ADMIN_LAYER_PREFIX}${portId}`;
  const innerLayerId = `${PORT_ADMIN_INNER_LAYER_PREFIX}${portId}`;

  const isPortShown = useMemo(() => {
    return doesLayerExist(layerId);
  }, [doesLayerExist, layerId]);

  const onShow = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch extended port data to get the polygon
      const result = await refetch();
      const portData = result.data;

      if (!portData || !portData.outer) {
        console.warn('Port has no outer polygon defined');
        setIsLoading(false);
        return;
      }

      // Create outer boundary layer (blue)
      const outerGeoJson = portOuterToGeoJson(portData);

      if (!outerGeoJson) {
        console.warn('Could not convert port to GeoJSON');
        setIsLoading(false);
        return;
      }

      const outerLayer = shapeToVectorLayer(
        outerGeoJson,
        displayProjection,
        dataProjection,
      );

      addLayer(
        outerLayer,
        portName || 'Port',
        true, // isRemovableFromLayerControl
        true, // canZoomToFromLayerControl
        true, // canShowLabel
        {
          id: layerId,
          name: portName,
          locode,
          type: 'port-admin',
          color: 'blue',
          transparency: 0.3,
        },
        layerId,
        false, // isLabelShown
        false, // clickable
        true, // hoverEnabled
        'below-ships', // position
        true, // contextMenuEnabled
      );

      // Create inner areas layer (orange, on top of outer)
      const innerGeoJson = portInnerToGeoJson(portData);

      if (innerGeoJson) {
        const innerLayer = shapeToVectorLayer(
          innerGeoJson,
          displayProjection,
          dataProjection,
        );

        addLayer(
          innerLayer,
          `${portName || 'Port'} - Inner Areas`,
          true,
          true,
          true,
          {
            id: innerLayerId,
            name: `${portName} Inner Areas`,
            locode,
            type: 'port-admin-inner',
            color: 'orange',
            transparency: 0.3,
          },
          innerLayerId,
          false, // isLabelShown
          false, // clickable
          true, // hoverEnabled
          'below-ships', // position
          true, // contextMenuEnabled
        );
      }

      zoomToLayer(outerLayer);
    } catch (error) {
      console.error('Failed to show port on map:', error);
    } finally {
      setIsLoading(false);
    }
  }, [
    refetch,
    addLayer,
    portId,
    portName,
    locode,
    zoomToLayer,
    layerId,
    innerLayerId,
    displayProjection,
    dataProjection,
  ]);

  const onRemove = useCallback(() => {
    removeLayerById(layerId);
    removeLayerById(innerLayerId);
  }, [layerId, innerLayerId, removeLayerById]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          id={`is-port-shown-button-${portName ?? portId ?? 'port'}`}
          size="smIcon"
          variant="ghost"
          onClick={isPortShown ? onRemove : onShow}
          disabled={isLoading}
        >
          {isPortShown ? (
            <PiEye className="size-4" />
          ) : (
            <PiEyeClosed className="size-4" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {isPortShown ? 'Remove port from map' : 'Show port on map'}
      </TooltipContent>
    </Tooltip>
  );
};
